// Simple transaction store for managing failed and RTGS transactions
type RTGSStatus = 'SENT_TO_BANK' | 'REVERSED' | 'MANUALLY_SETTLED'

interface RTGSTransaction {
  id: string
  mtoName: string
  mtoRef: string
  bankRef: string
  branchCode: string
  agentCode: string
  payoutType: 'FT' | 'IBFT' | 'OTC'
  amountFCY: string
  currencyFCY: string
  amountPKR: string
  remitterName: string
  beneficiaryName: string
  beneficiaryIBAN: string
  rtgsStatus: RTGSStatus
  sentToRTGSAt: string
  createdAt: string
  currentStage: string
  lifecycle: string
  transactionStatus: string
  status: string
  corridor: string
  purposeCode: string
  purposeText: string
}

interface RepushedTransaction {
  id: string
  mtoName: string
  mtoRef: string
  bankRef: string
  branchCode: string
  agentCode: string
  payoutType: 'FT' | 'IBFT' | 'OTC'
  amountFCY: string
  currencyFCY: string
  amountPKR: string
  corridor: string
  purposeCode: string
  purposeText: string
  remitterName: string
  beneficiaryName: string
  beneficiaryIBAN: string
  currentState: string
  lifecycleStatus: string
  createdAt: string
  updatedAt: string
  railRefs: any
  stateHistory: any[]
}

export interface PendingAmendment {
  id: string
  transactionId: string
  mtoName: string
  payoutType: 'FT' | 'IBFT' | 'OTC'
  amountFCY: string
  currencyFCY: string
  currentState: string
  amendmentReason: string
  requestedBy: string
  requestedAt: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
  originalAccount: string
  newAccount: string
  fetchedTitle: string
  checkerComment?: string
}

export interface ManualSettlementHistoryEntry {
  action: 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'RESUBMITTED' | 'DISCARDED'
  reason: string
  by: string
  at: string
}

export interface ManualSettlement {
  id: string
  transactionId: string
  mtoName: string
  payoutType: 'FT' | 'IBFT' | 'OTC'
  amountFCY: string
  currencyFCY: string
  currentState: string
  beneficiaryName: string
  beneficiaryIBAN: string
  makerReason: string
  requestedBy: string
  requestedAt: string
  status:
    | 'PENDING_CHECKER'
    | 'APPROVED'
    | 'REJECTED'
    | 'RESUBMITTED'
    | 'DISCARDED'
  checkerComment?: string
  checkerActedAt?: string
  checkerActedBy?: string
  resubmitReason?: string
  resubmittedAt?: string
  discardReason?: string
  discardedAt?: string
  history: ManualSettlementHistoryEntry[]
}

// Transaction field overrides applied when amendments are approved/rejected
export interface TransactionOverride {
  amendStatus: 'PENDING' | 'APPROVED' | 'REJECTED'
  manualSettlementStatus?:
    | 'PENDING_CHECKER'
    | 'APPROVED'
    | 'REJECTED'
    | 'RESUBMITTED'
    | 'DISCARDED'
  lifecycleStatus?: string
  currentState?: string
  beneficiaryIBAN?: string
  beneficiaryName?: string
  updatedAt?: string
  amendmentHistory?: any[]
}

// RTGS override applied when transactions are moved to RTGS or settled/reversed
export interface RTGSOverride {
  lifecycleStatus: string
  currentState: string
  rtgsStatus: string
  updatedAt: string
  comment?: string
  actionType?: string
}

class TransactionStore {
  private rtgsTransactions: RTGSTransaction[] = []
  private repushedTransactions: RepushedTransaction[] = []
  private amendments: PendingAmendment[] = []
  private manualSettlements: ManualSettlement[] = []
  private transactionOverrides: Map<string, TransactionOverride> = new Map()
  private rtgsOverrides: Map<string, RTGSOverride> = new Map()
  private listeners: Set<() => void> = new Set()

  // ===== MANUAL SETTLEMENT MANAGEMENT =====

  submitManualSettlement(transaction: any, reason: string) {
    const user = localStorage.getItem('username') || 'admin'
    const now = new Date().toISOString()

    const settlement: ManualSettlement = {
      id: `SETTLE-${Date.now()}`,
      transactionId: transaction.id,
      mtoName: transaction.mtoName,
      payoutType: transaction.payoutType,
      amountFCY: transaction.amountFCY,
      currencyFCY: transaction.currencyFCY,
      currentState: transaction.currentState,
      beneficiaryName: transaction.beneficiaryName,
      beneficiaryIBAN: transaction.beneficiaryIBAN,
      makerReason: reason,
      requestedBy: user,
      requestedAt: now,
      status: 'PENDING_CHECKER',
      history: [
        {
          action: 'SUBMITTED',
          reason: reason,
          by: user,
          at: now,
        },
      ],
    }

    this.manualSettlements.push(settlement)

    const existing = this.transactionOverrides.get(transaction.id) || {
      amendStatus: 'NONE' as any,
    }
    this.transactionOverrides.set(transaction.id, {
      ...existing,
      manualSettlementStatus: 'PENDING_CHECKER',
      updatedAt: now,
    })

    this.notifyListeners()
    return settlement
  }

  approveManualSettlement(settlementId: string, reason: string) {
    const settlement = this.manualSettlements.find((s) => s.id === settlementId)
    if (!settlement) return

    const user = localStorage.getItem('username') || 'checker'
    const now = new Date().toISOString()

    this.manualSettlements = this.manualSettlements.map((s) => {
      if (s.id === settlementId) {
        return {
          ...s,
          status: 'APPROVED',
          checkerComment: reason,
          checkerActedAt: now,
          checkerActedBy: user,
          history: [
            ...s.history,
            { action: 'APPROVED', reason, by: user, at: now },
          ],
        }
      }
      return s
    })

    const existing = this.transactionOverrides.get(
      settlement.transactionId,
    ) || { amendStatus: 'NONE' as any }
    this.transactionOverrides.set(settlement.transactionId, {
      ...existing,
      manualSettlementStatus: 'APPROVED',
      lifecycleStatus: 'SUCCESS',
      currentState: 'SUCCESS',
      updatedAt: now,
    })

    this.notifyListeners()
  }

  rejectManualSettlement(settlementId: string, reason: string) {
    const settlement = this.manualSettlements.find((s) => s.id === settlementId)
    if (!settlement) return

    const user = localStorage.getItem('username') || 'checker'
    const now = new Date().toISOString()

    this.manualSettlements = this.manualSettlements.map((s) => {
      if (s.id === settlementId) {
        return {
          ...s,
          status: 'REJECTED',
          checkerComment: reason,
          checkerActedAt: now,
          checkerActedBy: user,
          history: [
            ...s.history,
            { action: 'REJECTED', reason, by: user, at: now },
          ],
        }
      }
      return s
    })

    const existing = this.transactionOverrides.get(
      settlement.transactionId,
    ) || { amendStatus: 'NONE' as any }
    this.transactionOverrides.set(settlement.transactionId, {
      ...existing,
      manualSettlementStatus: 'REJECTED',
      updatedAt: now,
    })

    this.notifyListeners()
  }

  resubmitManualSettlement(settlementId: string, reason: string) {
    const settlement = this.manualSettlements.find((s) => s.id === settlementId)
    if (!settlement) return

    const user = localStorage.getItem('username') || 'admin'
    const now = new Date().toISOString()

    this.manualSettlements = this.manualSettlements.map((s) => {
      if (s.id === settlementId) {
        return {
          ...s,
          status: 'PENDING_CHECKER',
          resubmitReason: reason,
          resubmittedAt: now,
          history: [
            ...s.history,
            { action: 'RESUBMITTED', reason, by: user, at: now },
          ],
        }
      }
      return s
    })

    const existing = this.transactionOverrides.get(
      settlement.transactionId,
    ) || { amendStatus: 'NONE' as any }
    this.transactionOverrides.set(settlement.transactionId, {
      ...existing,
      manualSettlementStatus: 'PENDING_CHECKER',
      updatedAt: now,
    })

    this.notifyListeners()
  }

  discardManualSettlement(settlementId: string, reason: string) {
    const settlement = this.manualSettlements.find((s) => s.id === settlementId)
    if (!settlement) return

    const user = localStorage.getItem('username') || 'admin'
    const now = new Date().toISOString()

    this.manualSettlements = this.manualSettlements.map((s) => {
      if (s.id === settlementId) {
        return {
          ...s,
          status: 'DISCARDED',
          discardReason: reason,
          discardedAt: now,
          history: [
            ...s.history,
            { action: 'DISCARDED', reason, by: user, at: now },
          ],
        }
      }
      return s
    })

    const existing = this.transactionOverrides.get(
      settlement.transactionId,
    ) || { amendStatus: 'NONE' as any }
    this.transactionOverrides.set(settlement.transactionId, {
      ...existing,
      manualSettlementStatus: 'DISCARDED',
      updatedAt: now,
    })

    this.notifyListeners()
  }

  getPendingManualSettlements(): ManualSettlement[] {
    return this.manualSettlements.filter((s) => s.status === 'PENDING_CHECKER')
  }

  getRejectedManualSettlements(): ManualSettlement[] {
    return this.manualSettlements.filter((s) => s.status === 'REJECTED')
  }

  getManualSettlementForTransaction(
    transactionId: string,
  ): ManualSettlement | null {
    const settlements = this.manualSettlements.filter(
      (s) => s.transactionId === transactionId,
    )
    if (settlements.length === 0) return null
    // Return the most recent one
    return settlements[settlements.length - 1]
  }

  // ===== AMENDMENT MANAGEMENT =====

  submitAmendment(
    transaction: any,
    newAccount: string,
    fetchedTitle: string,
    reason: string,
  ) {
    const amendment: PendingAmendment = {
      id: `AMEND-${Date.now()}`,
      transactionId: transaction.id,
      mtoName: transaction.mtoName,
      payoutType: transaction.payoutType,
      amountFCY: transaction.amountFCY,
      currencyFCY: transaction.currencyFCY,
      currentState: transaction.currentState,
      amendmentReason: reason,
      requestedBy: localStorage.getItem('username') || 'admin',
      requestedAt: new Date().toISOString(),
      status: 'PENDING',
      originalAccount: transaction.beneficiaryIBAN,
      newAccount: newAccount,
      fetchedTitle: fetchedTitle,
    }
    this.amendments.push(amendment)

    // Set transaction override to PENDING
    this.transactionOverrides.set(transaction.id, {
      amendStatus: 'PENDING',
      updatedAt: new Date().toISOString(),
    })

    this.notifyListeners()
    return amendment
  }

  approveAmendment(amendmentId: string, checkerComment: string) {
    const amendment = this.amendments.find((a) => a.id === amendmentId)

    this.amendments = this.amendments.map((a) => {
      if (a.id === amendmentId) {
        return { ...a, status: 'APPROVED' as const, checkerComment }
      }
      return a
    })

    // If amendment found, update the transaction override with new account details
    if (amendment) {
      const existing =
        this.transactionOverrides.get(amendment.transactionId) ||
        ({} as TransactionOverride)
      const existingHistory = existing.amendmentHistory || []

      this.transactionOverrides.set(amendment.transactionId, {
        amendStatus: 'APPROVED',
        lifecycleStatus: 'IN_PROGRESS',
        currentState: 'TITLE_FETCH',
        beneficiaryIBAN: amendment.newAccount,
        beneficiaryName: amendment.fetchedTitle,
        updatedAt: new Date().toISOString(),
        amendmentHistory: [
          ...existingHistory,
          {
            timestamp: new Date().toISOString(),
            oldAccount: amendment.originalAccount,
            newAccount: amendment.newAccount,
            fetchedTitle: amendment.fetchedTitle,
            status: 'APPROVED',
            requestedBy: amendment.requestedBy,
            checkerComment: checkerComment,
          },
        ],
      })
    }

    this.notifyListeners()
  }

  rejectAmendment(amendmentId: string, checkerComment: string) {
    const amendment = this.amendments.find((a) => a.id === amendmentId)

    this.amendments = this.amendments.map((a) => {
      if (a.id === amendmentId) {
        return { ...a, status: 'REJECTED' as const, checkerComment }
      }
      return a
    })

    // Update transaction override - keep FAILED status but mark as REJECTED
    if (amendment) {
      const existing =
        this.transactionOverrides.get(amendment.transactionId) ||
        ({} as TransactionOverride)
      const existingHistory = existing.amendmentHistory || []

      this.transactionOverrides.set(amendment.transactionId, {
        amendStatus: 'REJECTED',
        updatedAt: new Date().toISOString(),
        amendmentHistory: [
          ...existingHistory,
          {
            timestamp: new Date().toISOString(),
            oldAccount: amendment.originalAccount,
            newAccount: amendment.newAccount,
            fetchedTitle: amendment.fetchedTitle,
            status: 'REJECTED',
            requestedBy: amendment.requestedBy,
            checkerComment: checkerComment,
          },
        ],
      })
    }

    this.notifyListeners()
  }

  // Register a demo amendment in the store so it can be tracked
  registerDemoAmendment(amendment: PendingAmendment) {
    // Only add if not already tracked
    if (!this.amendments.find((a) => a.id === amendment.id)) {
      this.amendments.push({ ...amendment })
    }
  }

  getPendingAmendments(): PendingAmendment[] {
    return this.amendments.filter((a) => a.status === 'PENDING')
  }

  getAllAmendments(): PendingAmendment[] {
    return [...this.amendments]
  }

  getAmendmentStatusForTransaction(
    transactionId: string,
  ): 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED' {
    const override = this.transactionOverrides.get(transactionId)
    if (override) return override.amendStatus

    const txnAmendments = this.amendments.filter(
      (a) => a.transactionId === transactionId,
    )
    if (txnAmendments.length === 0) return 'NONE'
    const latest = txnAmendments[txnAmendments.length - 1]
    return latest.status
  }

  // Get full transaction overrides (lifecycle, account, etc.)
  getTransactionOverride(transactionId: string): TransactionOverride | null {
    return this.transactionOverrides.get(transactionId) || null
  }

  // Get RTGS override for a transaction
  getRTGSOverride(transactionId: string): RTGSOverride | null {
    return this.rtgsOverrides.get(transactionId) || null
  }

  // ===== RTGS MANAGEMENT =====

  addToRTGS(transaction: any, notes?: string) {
    const rtgsTransaction: RTGSTransaction = {
      id: transaction.id,
      mtoName: transaction.mtoName,
      mtoRef: transaction.mtoRef,
      bankRef: transaction.bankRef,
      branchCode: transaction.branchCode,
      agentCode: transaction.agentCode,
      payoutType: transaction.payoutType,
      amountFCY: transaction.amountFCY,
      currencyFCY: transaction.currencyFCY,
      amountPKR: transaction.amountPKR,
      remitterName: transaction.remitterName,
      beneficiaryName: transaction.beneficiaryName,
      beneficiaryIBAN: transaction.beneficiaryIBAN,
      rtgsStatus: 'SENT_TO_BANK',
      sentToRTGSAt: new Date().toISOString(),
      createdAt: transaction.createdAt,
      currentStage: 'RTGS_POST',
      lifecycle: 'IN_PROGRESS',
      transactionStatus: 'PENDING_RTGS',
      status: 'IN_PROGRESS',
      corridor: transaction.corridor,
      purposeCode: transaction.purposeCode,
      purposeText: transaction.purposeText,
    }

    this.rtgsTransactions.push(rtgsTransaction)

    // Set RTGS override so TransactionsTable reflects the move
    this.rtgsOverrides.set(transaction.id, {
      lifecycleStatus: 'IN_PROGRESS',
      currentState: 'RTGS_POST',
      rtgsStatus: 'SENT_TO_BANK',
      updatedAt: new Date().toISOString(),
      comment: notes || undefined,
      actionType: 'MOVED_TO_RTGS',
    })

    this.notifyListeners()
  }

  repushTransaction(transaction: any) {
    const repushedTransaction: RepushedTransaction = {
      id: transaction.id,
      mtoName: transaction.mtoName,
      mtoRef: transaction.mtoRef,
      bankRef: transaction.bankRef,
      branchCode: transaction.branchCode,
      agentCode: transaction.agentCode,
      payoutType: transaction.payoutType,
      amountFCY: transaction.amountFCY,
      currencyFCY: transaction.currencyFCY,
      amountPKR: transaction.amountPKR,
      corridor: transaction.corridor,
      purposeCode: transaction.purposeCode,
      purposeText: transaction.purposeText,
      remitterName: transaction.remitterName,
      beneficiaryName: transaction.beneficiaryName,
      beneficiaryIBAN: transaction.beneficiaryIBAN,
      currentState: transaction.currentState,
      lifecycleStatus: 'IN_PROGRESS',
      createdAt: transaction.createdAt,
      updatedAt: new Date().toISOString(),
      railRefs: {},
      stateHistory: [
        ...transaction.stateHistory,
        {
          stateCode: transaction.currentState,
          status: 'PENDING',
          timestamp: new Date().toISOString(),
          primaryRef: `REPUSH-${transaction.id}`,
          attempts: (transaction.attemptCount || 0) + 1,
        },
      ],
    }

    this.repushedTransactions.push(repushedTransaction)
    this.notifyListeners()
  }

  getRTGSTransactions(): RTGSTransaction[] {
    return [...this.rtgsTransactions]
  }

  getRepushedTransactions(): RepushedTransaction[] {
    return [...this.repushedTransactions]
  }

  updateRTGSStatus(
    transactionIds: string[],
    status: RTGSStatus,
    comment?: string,
  ) {
    this.rtgsTransactions = this.rtgsTransactions.map((txn) => {
      if (transactionIds.includes(txn.id)) {
        return {
          ...txn,
          rtgsStatus: status,
          lifecycle:
            status === 'MANUALLY_SETTLED'
              ? 'SUCCESS'
              : status === 'REVERSED'
                ? 'FAILED'
                : 'IN_PROGRESS',
          transactionStatus:
            status === 'MANUALLY_SETTLED'
              ? 'FUNDS_TRANSFERRED'
              : status === 'REVERSED'
                ? 'REVERSED'
                : 'PENDING_RTGS',
          status:
            status === 'MANUALLY_SETTLED'
              ? 'COMPLETED'
              : status === 'REVERSED'
                ? 'REVERSED'
                : 'IN_PROGRESS',
        }
      }
      return txn
    })

    // Update RTGS overrides so TransactionsTable reflects the status change
    transactionIds.forEach((id) => {
      this.rtgsOverrides.set(id, {
        lifecycleStatus:
          status === 'MANUALLY_SETTLED'
            ? 'SUCCESS'
            : status === 'REVERSED'
              ? 'FAILED'
              : 'IN_PROGRESS',
        currentState: status === 'MANUALLY_SETTLED' ? 'SUCCESS' : 'RTGS_POST',
        rtgsStatus: status,
        updatedAt: new Date().toISOString(),
        comment: comment || undefined,
        actionType:
          status === 'MANUALLY_SETTLED'
            ? 'MANUAL_SUCCESS'
            : status === 'REVERSED'
              ? 'MANUAL_FAIL'
              : undefined,
      })
    })

    this.notifyListeners()
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener())
  }
}

export const transactionStore = new TransactionStore()
